import { createSlice } from '@reduxjs/toolkit';
import {
  getNodeInTree,
  updateNodeInTree,
} from '../../../components/Tree/helpers/helpers';
import { ReadOnlyArray } from '../../../utils/array/ReadOnlyArray';
import { extend } from '../../../utils/object/object';
import { ReadOnlyObject } from '../../../utils/object/ReadOnlyObject';
import { getCatalogId } from '../../../utils/tree/ids';
import { getPathById } from '../../../utils/tree/treeNode';
import { removeCatalogs } from '../../actions/catalog/catalog';
import { createTreesFromPreviewResult } from '../../actions/importitems/createTrees';
import { mergeStateReducerBuilder } from '../../reducers/generic/mergeState';
import {
  createReferenceEqualSelector,
  getCatalogIdFromTreeId,
  getIdFromArgument,
} from '../../selectors/utils';
import { highlightSuggestedCategoriesReducer } from './reducers/highlightSuggestedCategories';
import type { RootState } from '../..';
import type { NodeBase, NodeBaseItem } from '../../../entities/Node';
import { setMarket } from '../intl';

export type TreeCatalogs = {
  isFetching: boolean;
  treeData: NodeBase[];
  meta?: {
    totalNumberOfElements: number;
  };
  // For suggestion highlighting (set by highlightSuggestedCategories action)
  highlightFocusId?: string;
  highlightIndex?: string;
};

type TreeState = {
  catalogs: { [key: string]: TreeCatalogs | Record<string, never> };
};

const initialState: TreeState = {
  catalogs: {},
};

const treeSlice = createSlice({
  name: 'tree',
  initialState,
  reducers: {
    expandNode: {
      reducer(state, action) {
        const {
          meta: { tree },
          payload: { treeData },
        } = action;
        const catalogId = getCatalogId(tree);
        state.catalogs[catalogId] = extend(state.catalogs[catalogId], { treeData });
      },
      prepare(tree: NodeBase, treeData: NodeBaseItem, node, expanded) {
        return {
          meta: { tree },
          error: null,
          payload: {
            treeData,
            treeNode: {
              expanded,
              node,
            },
          },
        };
      },
    },
    updateCategoryImage(state, action) {
      const {
        catalogId,
        url,
        nodeId,
        comment = '',
        isGlobal = false,
      } = action.payload;
      const tree = state.catalogs[catalogId];
      const path = getPathById(nodeId) as string[];
      const res = getNodeInTree({ treeData: tree.treeData, path }) as NodeBaseItem;

      if (!res?.node?.category) return;

      const updatedCategory = {
        image: url,
        imageOverride: !isGlobal,
        imageComment: isGlobal ? '' : comment,
      };

      res.node.category = {
        ...res.node.category,
        ...updatedCategory,
      };

      const newTree = updateNodeInTree({
        node: res.node,
        path,
        treeData: tree.treeData,
      }) as NodeBase[];

      state.catalogs[catalogId].treeData = newTree;
    },
    updateProductCategoryImage(state, action) {
      const {
        catalogId,
        nodeId,
        isGlobal = false,
        itemNo,
        productType,
        productName,
        image,
      } = action.payload;
      const tree = state.catalogs[catalogId];
      const path = getPathById(nodeId) as string[];
      const res = getNodeInTree({ treeData: tree.treeData, path }) as NodeBaseItem;

      if (!res?.node?.category) return;

      const updatedCategory = {
        productImage: {
          image,
          itemNo,
          productType,
          productName,
        },
        productImageOverride: !isGlobal,
      };

      res.node.category = {
        ...res.node.category,
        ...updatedCategory,
      };

      const newTree = updateNodeInTree({
        node: res.node,
        path,
        treeData: tree.treeData,
      }) as NodeBase[];

      state.catalogs[catalogId].treeData = newTree;
    },
    updateData: {
      prepare(
        tree: string,
        treeData: NodeBase[],
        { selectNodeAtPath, ...extra } = {},
      ) {
        return {
          meta: {
            tree,
            selectNodeAtPath,
          },
          error: null,
          payload: {
            ...extra,
            treeData,
          },
        };
      },
      reducer(state, action) {
        const {
          meta: { tree },
          payload,
        } = action;
        const catalogId = getCatalogId(tree);
        state.catalogs[catalogId] = extend(state.catalogs[catalogId], payload);
      },
    },
    highlightSuggestedCategories: {
      prepare(
        tree: string,
        highlight: boolean,
        highlightItem?: '+' | '-' | '',
        categoryIds?: string[],
      ) {
        return {
          meta: { tree },
          payload: { highlight, highlightItem, categoryIds },
        };
      },
      reducer: highlightSuggestedCategoriesReducer,
    },

    setIsFetching: {
      prepare(tree: string, isFetching) {
        return { meta: { tree, isFetching }, payload: null, error: null };
      },
      reducer(state, action) {
        const {
          meta: { tree, isFetching },
        } = action;
        const catalogId = getCatalogId(tree);
        state.catalogs[catalogId].isFetching = isFetching;
      },
    },
    pasteNode: {
      prepare(tree: string, treeData: NodeBase) {
        return {
          meta: { tree },
          payload: {
            treeData,
          },
          error: null,
        };
      },
      reducer(state, action) {
        const {
          meta: { tree },
          payload: { treeData },
        } = action;
        const catalogId = getCatalogId(tree);
        state.catalogs[catalogId] = extend(state.catalogs[catalogId], { treeData });
      },
    },
  },
  extraReducers: (builder) =>
    mergeStateReducerBuilder('state.tree', builder)
      .addCase(setMarket, () => ({ ...initialState }))
      .addCase(removeCatalogs, (state, action) => {
        const catalogIds = action.payload;
        catalogIds.forEach((catalogId) => {
          delete state.catalogs[catalogId];
        });
      })
      .addCase(createTreesFromPreviewResult, (state, action) => {
        const { payload } = action;
        const data = Object.entries(payload.trees).reduce((t, [treeId, treeObj]) => {
          const catalogId = getCatalogId(treeId);
          if (!catalogId) {
            return t;
          }
          return Object.assign(t, { [catalogId]: treeObj });
        }, {});

        Object.assign(state.catalogs, data);
      }),
});

export const {
  expandNode,
  highlightSuggestedCategories,
  pasteNode,
  setIsFetching,
  updateCategoryImage,
  updateProductCategoryImage,
  updateData,
} = treeSlice.actions;

// Re-export search actions from treeSearch slice for backward compatibility
export {
  clearSearch,
  moveSearchNext,
  moveSearchPrevious,
  search,
  searchBySuggestionId,
  searchSetConditionByFlag,
  searchSetConditionByText,
  searchUpdate,
  toggleSearchPanel,
} from '../treeSearch';

export default treeSlice.reducer;

const treeSelector = (state: RootState) => state.tree;

export const selectTreeDataByCatalogId = createReferenceEqualSelector(
  treeSelector,
  getIdFromArgument,
  (state, catalogId) => state?.catalogs[catalogId]?.treeData || ReadOnlyArray.Empty,
);
export const selectTreeDataByTreeId = createReferenceEqualSelector(
  treeSelector,
  getCatalogIdFromTreeId,
  (state, catalogId) => state.catalogs[catalogId]?.treeData || ReadOnlyArray.Empty,
);

export const selectIsFetchingTree = createReferenceEqualSelector(
  treeSelector,
  getIdFromArgument,
  (state, catalogId) => state.catalogs[catalogId]?.isFetching,
);

export const selectTreeObject = createReferenceEqualSelector(
  treeSelector,
  getCatalogIdFromTreeId,
  (state, catalogId) => state.catalogs[catalogId] || ReadOnlyObject.Empty,
);
export const selectTreeTotalNumberOfElements = createReferenceEqualSelector(
  treeSelector,
  getIdFromArgument,
  (state, catalogId) => state.catalogs[catalogId]?.meta?.totalNumberOfElements || 0,
);

// Selector for suggestion highlighting (uses tree state, not search state)
export const selectSuggestionFocusIdForCatalog = createReferenceEqualSelector(
  treeSelector,
  getIdFromArgument,
  (state, catalogId) => state.catalogs[catalogId]?.highlightFocusId,
);

// Re-export search selectors from treeSearch slice for backward compatibility
export {
  selectSearchCatalogHits,
  selectSearchFocusForCatalog,
  selectSearchFocusIdForCatalog,
  selectSearchFocusIdForTree,
  selectSearchFocusIndexForTree,
  selectSearchItemHit,
  selectSearchMatchesCountForTree,
  selectSearchMatchesForTree,
  selectSearchPanelOpenForTree,
  selectSearchQueryForTree,
  selectSearchSuggestionsForTree,
} from '../treeSearch';
